import { Tooltip } from 'antd';
import ErrorHandler from 'common/error/components/error-handler';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Route, Routes, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Flex, Icon } from 'ui';
import Table from '../../../common/table/table';
import useConfirmationMessage from '../../../utils/hooks/useConfirmationMessage';
import { deleteWipCode, fetchWipCodes } from '../../containers/wip-codes/wip-codes-actions';
import WipCodesSidepanel from './wip-codes-sidepanel';

const AddButton = styled(Button)`
  text-transform: uppercase;
`;

const Toolbar = styled(Flex)`
  height: 55px;
  background-color: rgb(245, 248, 250) !important;
  margin: 0 -20px 25px;
  padding-left: 20px;
`;

const Icons = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 20px;
`;

const StyledIcon = styled(Icon)`
  color: ${p => p.disabled ? 'rgba(0, 0, 0, 0.3)' : p.theme.colors.theme};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
`;

const WipCodes = ({ intl }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showConfirm] = useConfirmationMessage();
  const [currentScope, setCurrentScope] = useState();
  const serviceCenterId = useSelector(state => state.serviceCenter.activeServiceCenter);

  const {
    wipCodes,
    isLoading,
    wipCodeError,
  } = useSelector(state => state.wipCodes);

  useEffect(() => {
    dispatch(fetchWipCodes());
  }, []);

  useEffect(() => {
    if (serviceCenterId) {
      setCurrentScope(serviceCenterId);
    }
  }, [serviceCenterId]);

  const wipCodesOfCurrentScope =
    currentScope ? wipCodes?.find(c => c.scope === currentScope) : [];

  const columns = [
    {
      id: 'actions',
      displayName: '',
      fieldName: '',
      width: '60px',
      renderFn: r => <Icons>
        <Tooltip
          title={intl.formatMessage({ id: 'wipCodes.EDIT_WIP_CODE' })}
          key='edit'
          mouseEnterDelay={0.5}
        >
          <div>
            <StyledIcon
              type='pen'
              onClick={r?.isEditable ?
                () => navigate(`wipCode/${r.id}`)
                : null}
              disabled={!r?.isEditable}
            />
          </div>
        </Tooltip>
        <Tooltip
          title={intl.formatMessage({ id: 'wipCodes.DELETE_WIP_CODE' })}
          key='delete'
          mouseEnterDelay={0.5}
        >
          <div>
            <StyledIcon
              type='trash-can'
              onClick={r?.isEditable ?
                () => showConfirm(() => dispatch(deleteWipCode({ scope: currentScope, id: r.id })))
                : null}
              disabled={!r?.isEditable}
            />
          </div>
        </Tooltip>
      </Icons>,
    },
    {
      id: 'code',
      displayName: intl.formatMessage({ id: 'wipCodes.CODE' }),
      renderFn: row => row.code || '',
      width: '200px',
    },
    {
      id: 'description',
      displayName: intl.formatMessage({ id: 'wipCodes.DESCRIPTION' }),
      renderFn: row => row.description || '',
      width: '300px',
      tooltip: row => row.description,
    },
    {
      id: 'scope',
      displayName: intl.formatMessage({ id: 'wipCodes.SCOPE' }),
      renderFn: row => row.scopeDescription || '',
      width: '150px',
    },
    {
      id: 'action',
      displayName: intl.formatMessage({ id: 'wipCodes.ACTION' }),
      renderFn: row => row.action || '',
    },
  ];

  return (
    <>
      <ErrorHandler type='notification' error={wipCodeError} />
      <Toolbar aic jcfs ffrn>
        <div>
          <Link to={'wipcode'}>
            <AddButton
              type='secondary'
              disabled={!wipCodesOfCurrentScope?.isEditable}
            >
              {intl.formatMessage({ id: 'wipCodes.ADD_WIP_CODE' })}
            </AddButton>
          </Link>
        </div>
      </Toolbar>
      <Table
        columns={columns}
        data={wipCodes?.reduce((t, c) => [
          ...t || [],
          ...c?.wipCodes.map(w => ({
            ...w,
            scopeDescription: c.scopeDescription,
            isEditable: c.isEditable,
          })) || [],
        ], []) || []}
        labelInColumn={true}
        isLoadingData={isLoading}
        width='65%'
      />
      <Routes>
        <Route
          path={'wipcode/:id?'}
          element={<WipCodesSidepanel scope={currentScope} />}
        />
      </Routes>
    </>
  );
};

WipCodes.propTypes = {
  intl: PropTypes.object,
};

export default injectIntl(WipCodes);
